import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass, Question, WhatsappLogo, Phone, EnvelopeSimple, ChatCircleDots, Plus } from "@phosphor-icons/react";
import { PageLoader, EmptyState, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function Support() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const audience = user?.role === "organizer" ? "organizer" : "customer";
  const [faqs, setFaqs] = useState([]);
  const [contact, setContact] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "General", message: "" });

  const load = () => {
    Promise.all([
      api.get(`/support/faqs?audience=${audience}`),
      api.get("/support/contact"),
      api.get("/support/tickets"),
    ]).then(([f, c, t]) => { setFaqs(f.data.faqs); setContact(c.data); setTickets(t.data.tickets); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Prefill an inquiry when arriving from Services "Inquire"
  useEffect(() => {
    const service = params.get("service");
    if (service) {
      const evt = params.get("event") || "";
      setForm({
        subject: `Service inquiry: ${service}`,
        category: params.get("inquiry_type") || "service_inquiry",
        message: `Hi EventVexa team,\n\nOrganizer: ${user?.name || ""} (${user?.email || ""})\n` +
                 (evt ? `Event: ${evt}\n` : "") + `Service: ${service}\n\nI'd like to know more about this service. Please get in touch.`,
      });
      setOpen(true);
    }
  }, [params, user]);

  const filtered = q ? faqs.filter((f) => (f.question + f.answer).toLowerCase().includes(q.toLowerCase())) : faqs;

  const createTicket = async () => {
    try { await api.post("/support/tickets", form); toast.success("Support ticket created — our team will respond shortly."); setOpen(false); setForm({ subject: "", category: "General", message: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const inquiryService = params.get("service");
  const waText = encodeURIComponent(`Hi EventVexa team, I'm ${user?.name || "an organizer"} (${audience})` +
    (inquiryService ? ` — I'd like to inquire about the "${inquiryService}" service.` : " and I need help with "));

  if (loading) return <PageLoader />;

  return (
    <div className="mx-auto max-w-4xl px-4 md:px-6 py-8">
      <div className="overline mb-1">We're here to help</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Help & Support</h1>

      {/* contact channels */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Channel icon={WhatsappLogo} label="WhatsApp" value={contact?.whatsapp} color="text-emerald-600"
          href={contact?.whatsapp ? `https://wa.me/${(contact.whatsapp).replace(/\D/g, "")}?text=${waText}` : null}
          testid="channel-whatsapp" />
        <Channel icon={Phone} label="Call Helpline" value={contact?.phone} color="text-primary"
          href={contact?.phone ? `tel:${contact.phone.replace(/\s/g, "")}` : null} testid="channel-call" />
        <Channel icon={EnvelopeSimple} label="Email" value={contact?.email} color="text-navy"
          href={contact?.email ? `mailto:${contact.email}?subject=${encodeURIComponent(`EventVexa support — ${user?.name || audience}`)}&body=${encodeURIComponent(`Hi EventVexa team,\n\nI'm ${user?.name || "a user"} (${audience}, ${user?.email || ""}). I need help with:\n\n`)}` : null}
          testid="channel-email" />
        <Channel icon={ChatCircleDots} label="Working hours" value={contact?.hours} color="text-amber-600" testid="channel-hours" />
      </div>

      <div className="relative mb-4">
        <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search FAQs…" className="pl-11 h-11 rounded-full" data-testid="faq-search" />
      </div>

      {filtered.length > 0 ? (
        <Accordion type="single" collapsible className="rounded-xl border border-border bg-card px-4">
          {filtered.map((f) => (
            <AccordionItem key={f.id} value={f.id}>
              <AccordionTrigger className="text-left" data-testid={`faq-${f.id}`}>{f.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : <EmptyState icon={Question} title="No FAQs found" subtitle="Raise a ticket and our team will help you." testid="no-faqs" />}

      <div className="flex items-center justify-between mt-10 mb-4">
        <h2 className="font-heading text-xl font-semibold">Your support tickets</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-ticket-btn"><Plus className="mr-1.5" /> New Support Ticket</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Raise a support ticket</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" data-testid="ticket-subject" /></div>
              <div><Label>Message</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" rows={4} data-testid="ticket-message" /></div>
              <DialogFooter><Button onClick={createTicket} className="w-full rounded-full" data-testid="submit-ticket">Submit</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {tickets.length === 0 ? (
        <p className="text-sm text-muted-foreground">No tickets yet.</p>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <div key={t.id} className="p-4 rounded-xl border border-border bg-card" data-testid={`support-ticket-${t.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium">{t.subject}</div>
                <StatusBadge status={t.status} />
              </div>
              <div className="text-sm text-muted-foreground mt-1">{t.messages?.[t.messages.length - 1]?.text}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Channel({ icon: Icon, label, value, color, href, testid }) {
  const inner = (
    <>
      <Icon size={22} className={color} weight="fill" />
      <div className="text-xs text-muted-foreground mt-2">{label}</div>
      <div className="font-medium text-sm truncate">{value || "—"}</div>
    </>
  );
  if (href && value) {
    return (
      <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testid}
        className="p-4 rounded-xl border border-border bg-card transition-colors hover:border-primary/40 hover:bg-primary/5 block">
        {inner}
      </a>
    );
  }
  return <div className="p-4 rounded-xl border border-border bg-card" data-testid={testid}>{inner}</div>;
}
